﻿// app/javascript/lib/format.ts

/**
 * Formats a numeric percentile into a proper ordinal string.
 * e.g. 100 -> "100th Percentile", 21 -> "21st Percentile", 42.3 -> "42.3 Percentile"
 */
export function formatPercentile(pct: number | null | undefined): string {
  if (pct == null) return "N/A"
  const rounded = Math.round(pct * 10) / 10
  const intVal = Math.floor(rounded)
  const isWholeNumber = rounded % 1 === 0

  if (isWholeNumber) {
    const lastTwo = intVal % 100
    if (lastTwo >= 11 && lastTwo <= 13) return `${intVal}th Percentile`
    switch (intVal % 10) {
      case 1: return `${intVal}st Percentile`
      case 2: return `${intVal}nd Percentile`
      case 3: return `${intVal}rd Percentile`
      default: return `${intVal}th Percentile`
    }
  }
  return `${rounded} Percentile`
}

/**
 * Formats time in seconds to "Xh Ym Zs", "Xm Ys", or "Xs"
 */
export function formatTime(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return "0s"
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  if (h > 0) return `${h}h ${m}m ${s}s`
  if (m > 0) return `${m}m ${s}s`
  return `${s}s`
}

/**
 * Letter grade derived from accuracy or score percentage.
 * A >= 80%, B 60-79%, C 40-59%, D < 40%
 */
export function formatGrade(percentage: number | null | undefined): { grade: string; label: string; token: string } {
  if (percentage == null) return { grade: "—", label: "Not Graded", token: "muted" }
  if (percentage >= 80) return { grade: "A", label: "Excellent", token: "secondary" }
  if (percentage >= 60) return { grade: "B", label: "Good", token: "primary" }
  if (percentage >= 40) return { grade: "C", label: "Average", token: "tertiary" }
  return { grade: "D", label: "Needs Improvement", token: "destructive" }
}

/**
 * Formats a number, rounding to 1 decimal if needed, stripping .0 suffix.
 */
export function formatNumber(n: number | null | undefined): string {
  if (n == null) return "0"
  const rounded = Math.round(n * 10) / 10
  return rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1)
}
